<template>
  <div class="flex flex-wrap justify-between items-center gap-y-8">
    <div class="flex gap-4 flex-wrap">
      <BittsQuickFilter
        v-for="filter in filters"
        v-model="filterValues[filter.value]"
        :key="filter.value"
        :filter="filter"
        :open-on-mount="false"
        :show-search="filter.useSearch ?? false"
        :show-header="false"
        :no-custom-value="true"
        :disabled="isActionBarDisabled"
        size="medium"
        :options="filter.options"
        :use-select-all="shouldUseSelectAll(filter.value)"
        @clear-filter="handleClearFilter(filter.value)"
        @apply-filter="handleApplyFilter"
        @remove-filter="handleRemoveFilter(filter.value)"
      >
        <template #option-prefix-icon="{ item }">
          <BittsAvatar
            v-if="getPresetLogoSvg(filter.value, item.value)"
            size="x-small"
            shape="square"
            :is-entity="true"
            :custom-svg="getPresetLogoSvg(filter.value, item.value)"
          />
        </template>
      </BittsQuickFilter>
    </div>
    <div class="flex gap-6">
      <BittsInput
        v-model="searchValue"
        name="search"
        :placeholder="searchPlaceholder"
        type="search"
        size="small"
        :disabled="isActionBarDisabled"
        @update:model-value="handleSearch"
      />
      <BittsButton
        v-if="showEditButton"
        class="text-14"
        text="Bulk Edit"
        size="small"
        :disabled="isActionBarDisabled"
        @click="emit('edit-clicked')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  BittsAvatar,
  BittsButton,
  BittsInput,
  BittsQuickFilter,
} from '@crossbeam/bitts';
import { BittsIcon } from '@crossbeam/bitts/src/types';
import { EVENT_SITES } from '@crossbeam/itly';

import { useDebounceFn } from '@vueuse/core';
import { computed, onMounted, reactive, ref } from 'vue';

import useIteratively from '@/composables/useIteratively';
import {
  CROSSBEAM_DEFAULT_PRESET_TYPE,
  NEW_SHARING_TYPES_MAP,
} from '@/constants/data_shares';
import { useDataSharePresetsStore, useFeedsStore } from '@/stores';
import type { DataSharePreset } from '@/types/data_share_presets';

type FilterOption = {
  label: string;
  value: string | number;
  hidden?: boolean;
  dataSourceType?: string;
};

type Filter = {
  value: string;
  label: string;
  faIcon: BittsIcon;
  options: FilterOption[];
  datatype?: 'datetime' | 'number' | 'lookup' | 'text' | 'radio';
  useSearch?: boolean;
  preserveOrder?: boolean;
};

type FilterValues = Record<string, (string | number)[]>;

type Props = {
  isActionBarDisabled?: boolean;
  searchPlaceholder?: string;
  firstFilter: Filter;
  initialFilterKeys: string[];
  showEditButton?: boolean;
  includeSharedFieldsFilter?: boolean;
  includeOverridesFilter?: boolean;
  initialFilterValues?: FilterValues;
  feedId?: number;
  sourceId?: number;
  /** Event site for dataSharingAction (e.g. default sharing, my data, partner populations). */
  eventSite?: string;
};

const props = withDefaults(defineProps<Props>(), {
  isActionBarDisabled: false,
  searchPlaceholder: 'Search',
  showEditButton: true,
  includeSharedFieldsFilter: true,
  includeOverridesFilter: false,
  initialFilterValues: undefined,
  feedId: undefined,
  sourceId: undefined,
  eventSite: undefined,
});

const emit = defineEmits<{
  'search-applied': [value: string];
  'filters-applied': [filterValues: FilterValues];
  'edit-clicked': [];
}>();

const dataSharePresetsStore = useDataSharePresetsStore();
const feedsStore = useFeedsStore();
const { iteratively } = useIteratively();

function getDataSourceTypes(presets: DataSharePreset[]) {
  return new Set(
    presets.map(
      (preset) => feedsStore.getFeedById(preset.feed_id)?.integration.type,
    ),
  );
}

/* An option stands for every preset with its name, possibly on several data
   sources, so it only shows a logo when they all share one. */
function getSharedDataSourceType(presets: DataSharePreset[]) {
  const types = getDataSourceTypes(presets);
  return types.size === 1 ? [...types][0] : undefined;
}

const presetOptions = computed<FilterOption[]>(() => {
  // If feedId is provided, filter presets by data source
  const presetsToUse =
    props.feedId !== undefined
      ? dataSharePresetsStore.getFilteredPresetsByFeeId(
          props.feedId,
          props.sourceId,
        )
      : dataSharePresetsStore.allPresets;

  const namedPresetsByName = new Map<string, DataSharePreset[]>();
  presetsToUse.forEach((preset) => {
    if (preset.preset_type !== 'named_preset' || !preset.name) return;
    namedPresetsByName.set(preset.name, [
      ...(namedPresetsByName.get(preset.name) ?? []),
      preset,
    ]);
  });

  // Logos only tell options apart when more than one data source has presets.
  const hasSeveralDataSources = getDataSourceTypes(presetsToUse).size > 1;
  function getOptionDataSourceType(presets: DataSharePreset[]) {
    return hasSeveralDataSources ? getSharedDataSourceType(presets) : undefined;
  }

  const options: FilterOption[] = [...namedPresetsByName].map(
    ([name, presets]) => ({
      label: name,
      value: name,
      dataSourceType: getOptionDataSourceType(presets),
    }),
  );

  const crossbeamPresets = presetsToUse.filter(
    (preset) => preset.preset_type === CROSSBEAM_DEFAULT_PRESET_TYPE,
  );
  if (crossbeamPresets.length) {
    options.unshift({
      label: dataSharePresetsStore.crossbeamPresetLabel,
      value: CROSSBEAM_DEFAULT_PRESET_TYPE,
      dataSourceType: getOptionDataSourceType(crossbeamPresets),
    });
  }

  return options;
});

function getPresetLogoSvg(filterKey: string, optionValue: string | number) {
  if (filterKey !== 'field_presets') return undefined;
  const dataSourceType = presetOptions.value.find(
    (option) => option.value === optionValue,
  )?.dataSourceType;
  return dataSourceType && `${dataSourceType}Icon`;
}

const filters = computed<Filter[]>(() => {
  const baseFilters: Filter[] = [
    props.firstFilter,
    {
      value: 'sharing_level',
      options: Object.values(NEW_SHARING_TYPES_MAP).map((option) => ({
        label: option.label,
        value: option.value,
      })),
      label: 'Sharing Level',
      faIcon: ['fak', 'settings'] as BittsIcon,
      preserveOrder: true,
    },
  ];

  if (props.includeSharedFieldsFilter) {
    baseFilters.push({
      value: 'field_presets',
      options: presetOptions.value,
      label: 'Field Presets',
      faIcon: ['fak', 'slider'] as BittsIcon,
      useSearch: true,
    });
  }

  if (props.includeOverridesFilter) {
    baseFilters.push({
      value: 'overrides',
      options: [
        { label: 'All', value: 'all', hidden: true },
        { label: 'True', value: 'true' },
        { label: 'False', value: 'false' },
      ],
      label: 'Overrides',
      faIcon: ['fak', 'override'] as BittsIcon,
      datatype: 'radio',
      useSearch: false,
    });
  }

  return baseFilters;
});

const filterValues = reactive<FilterValues>(
  props.initialFilterKeys.reduce((acc, key) => {
    acc[key] = props.initialFilterValues?.[key] ?? [];
    return acc;
  }, {} as FilterValues),
);

const searchValue = ref('');

onMounted(() => {
  if (props.initialFilterValues) {
    emit('filters-applied', { ...filterValues });
  }
});

const dataSharingEventSite = computed(
  () => props.eventSite ?? EVENT_SITES.DATA_SHARING_POPULATION_TAB_ACTIONS,
);

const debouncedSearch = useDebounceFn((value: string) => {
  iteratively.dataSharingAction({
    event_site: dataSharingEventSite.value,
    metadata: { action: 'search', search_term: value },
  });
  emit('search-applied', value);
}, 300);

function handleSearch(value: string) {
  debouncedSearch(value);
}

function trackFiltersAction(action: 'applied_filters' | 'cleared_filters') {
  iteratively.dataSharingAction({
    event_site: dataSharingEventSite.value,
    metadata: {
      action,
      filter_values: { ...filterValues },
    },
  });
}

function handleClearFilter(filterKey: string) {
  filterValues[filterKey] = [];
  trackFiltersAction('cleared_filters');
  emit('filters-applied', { ...filterValues });
}

function handleRemoveFilter(filterKey: string) {
  filterValues[filterKey] = [];
  trackFiltersAction('cleared_filters');
  emit('filters-applied', { ...filterValues });
}

function handleApplyFilter() {
  trackFiltersAction('applied_filters');
  emit('filters-applied', { ...filterValues });
}

function shouldUseSelectAll(filterValue: string) {
  // Enable select-all for population_type (partner tags) and field_presets (presets)
  return filterValue === 'partner_tag' || filterValue === 'field_presets';
}

function clearAllFilters() {
  // Clear all filter values
  Object.keys(filterValues).forEach((key) => {
    filterValues[key] = [];
  });
  // Clear search value
  searchValue.value = '';
  trackFiltersAction('cleared_filters');
  // Emit the cleared state
  emit('filters-applied', { ...filterValues });
  emit('search-applied', '');
}

defineExpose({
  clearAllFilters,
});
</script>
